/**
 * Main composable for image generation functionality
 */

import { nextTick, ref } from 'vue'
import { useDialog, useMessage } from 'naive-ui'
import type {
  GeneratedImage,
  GenerationItem,
  ImageGenerationSettings,
  ImageGenerationState,
  ImageSizeOption,
} from '../types'
import {
  NANO_BANANA_ASPECT_RATIOS,
  NS11_ASPECT_RATIOS,
  STORAGE_KEYS,
  createDefaultSettings,
  createDefaultState,
  getImageSizeOptions as getImageSizeOptionsFromConstants,
  getStoredConversationId,
  getStoredGenerationHistory,
  setStoredConversationId,
  setStoredGenerationHistory,
} from '../constants'
import { generateImage as apiGenerateImage, deleteConversationMessage, generateImageNanoBanana, generateImageSelfHost } from '@/api/image'
import type { ImageGenerationRequest, ImageGenerationRequestNanoBanana, ImageGenerationRequestSelfHost } from '@/api/image'
import { t } from '@/locales'

/**
 * Utility function to clear image generation storage (can be used independently)
 */
export function clearImageGenerationStorage() {
  try {
    localStorage.removeItem(STORAGE_KEYS.CONVERSATION_ID)
    localStorage.removeItem(STORAGE_KEYS.GENERATION_HISTORY)
  }
  catch (error) {
    console.warn('Failed to clear image generation storage:', error)
  }
}

export function useImageGeneration() {
  const message = useMessage()
  const dialog = useDialog()

  // State
  const state = ref<ImageGenerationState>(createDefaultState())
  const settings = ref<ImageGenerationSettings>(createDefaultSettings())

  // Storage functions
  const saveToStorage = () => {
    if (state.value.conversationId)
      setStoredConversationId(state.value.conversationId)

    if (state.value.generationHistory.length > 0)
      setStoredGenerationHistory(state.value.generationHistory)
  }

  const loadFromStorage = () => {
    const savedConversationId = getStoredConversationId()
    if (savedConversationId)
      state.value.conversationId = savedConversationId

    const savedHistory = getStoredGenerationHistory<GenerationItem>()
    if (savedHistory.length > 0)
      state.value.generationHistory = savedHistory
  }

  const clearStorage = () => {
    clearImageGenerationStorage()
  }

  // Scroll function
  const scrollToBottom = async () => {
    await nextTick()
    setTimeout(() => {
      const scrollArea = document.querySelector('.flex-1.overflow-y-auto')
      if (scrollArea) {
        scrollArea.scrollTo({
          top: scrollArea.scrollHeight,
          behavior: 'smooth',
        })
      }
    }, 100)
  }

  // Image size options based on model
  const getImageSizeOptions = (): ImageSizeOption[] => {
    return getImageSizeOptionsFromConstants(settings.value.selectedModel)
  }

  // Generate image
  const generateImage = async () => {
    // Allow generation if system_instruction is provided (for tools) even without prompt
    const hasPrompt = state.value.prompt.trim().length > 0
    const hasUsedTool = settings.value.tool !== null && settings.value.tool !== 'none'

    if (!hasPrompt && !hasUsedTool)
      return

    // Check if file upload is required for GPT_IMAGE_EDIT model
    if (settings.value.selectedModel === 'GPT_IMAGE_EDIT' && state.value.uploadedFiles.length === 0) {
      message.warning(
        t('imageGeneration.requiresFileUpload')
        || 'Model chỉnh sửa ảnh yêu cầu bạn tải lên ít nhất một ảnh. Vui lòng nhấn vào biểu tượng thư viện để tải ảnh lên.',
        { duration: 5000 },
      )
      return
    }

    state.value.isGenerating = true
    await scrollToBottom()

    try {
      let response

      if (settings.value.selectedModel === 'UNI_IMAGE') {
        const aspectRatio = NS11_ASPECT_RATIOS[settings.value.aspectRatioIndex]
        const ns11Request: ImageGenerationRequestSelfHost = {
          prompt: state.value.prompt,
          negative: settings.value.negativePrompt || '',
          style: settings.value.ns11Style || '',
          pages: settings.value.ns11Pages,
          width: aspectRatio.width,
          height: aspectRatio.height,
          conversation_id: state.value.conversationId || undefined,
          model: 'UNI_IMAGE',
        }

        response = await generateImageSelfHost(ns11Request)
      }
      else if (settings.value.selectedModel === 'NANO_BANANA' || settings.value.selectedModel === 'NANO_BANANA_PRO') {
        const aspectRatio = NANO_BANANA_ASPECT_RATIOS[settings.value.aspectRatioIndex]

        // Map aspect ratio to API format using exact ratios
        const aspectRatioMap: Record<string, string> = {
          1: '1:1', // 1/1
          0.6666666666666666: '2:3', // 2/3
          1.5: '3:2', // 3/2
          0.75: '3:4', // 3/4
          1.3333333333333333: '4:3', // 4/3
          0.5625: '9:16', // 9/16
          1.7777777777777777: '16:9', // 16/9
          2.3333333333333335: '21:9', // 21/9
        }

        const ratioKey = aspectRatio.ratio.toString()
        const aspectRatioValue = aspectRatioMap[ratioKey] || '1:1'

        // Use user-selected image size from settings
        const imageSize = settings.value.nanoBananaImageSize

        const nanoBananaRequest: ImageGenerationRequestNanoBanana = {
          prompt: state.value.prompt || '', // Ensure prompt is at least empty string
          conversation_id: state.value.conversationId || undefined,
          image_size: imageSize,
          aspect_ratio: aspectRatioValue as any,
          style: settings.value.ns11Style || 'cinematic',
          model: settings.value.selectedModel as 'NANO_BANANA' | 'NANO_BANANA_PRO',
          files: state.value.uploadedFiles.length > 0 ? state.value.uploadedFiles : undefined,
          tool: settings.value.tool,
        }

        response = await generateImageNanoBanana(nanoBananaRequest)
      }
      else {
        const request: ImageGenerationRequest = {
          query: state.value.prompt,
          model: settings.value.selectedModel,
          number_of_image: 1,
          image_size: settings.value.selectedImageSize,
          image_to_edit:
            settings.value.selectedModel === 'GPT_IMAGE_EDIT'
              ? (state.value.uploadedFiles.length > 0 ? state.value.uploadedFiles : [])
              : [],
          image_style: settings.value.selectedStyle || 'vivid',
          conversation_id: state.value.conversationId || undefined,
          background_image: settings.value.selectedModel === 'GPT_IMAGE_GENERATE'
            ? settings.value.backgroundImage
            : undefined,
        }

        if (settings.value.selectedModel === 'GPT_IMAGE_EDIT') {
          request.image_to_edit = state.value.uploadedFiles.length > 0
            ? state.value.uploadedFiles
            : []
        }
        else {
          request.image_to_edit = []
        }

        response = await apiGenerateImage(request)
      }

      if (response && response.success && response.data) {
        const files = response.data.files || []
        const urls = response.data.urls || []

        if ((files.length > 0) || (urls.length > 0)) {
          const images: GeneratedImage[] = files.length > 0
            ? files.map((file: any) => ({
              id: String(file.id),
              url: file.url,
              agent_generate_image_history_id: file.agent_generate_image_history_id,
              is_deleted: file.is_deleted,
            }))
            : urls.map((url: string, index: number) => ({
              id: `${Date.now()}-${index}`,
              url,
            }))

          const newGeneration: GenerationItem = {
            id: String(response.data.id),
            query: response.data.query,
            model: response.data.model,
            image_size: response.data.image_size,
            image_style: response.data.image_style,
            number_of_image: response.data.number_of_image,
            conversation_id: response.data.conversation_id,
            generation_time: response.data.generation_time,
            created_at: response.data.created_at,
            images,
            // Save settings for regeneration
            negativePrompt: settings.value.negativePrompt,
            ns11Style: settings.value.ns11Style,
            ns11Pages: settings.value.ns11Pages,
            nanoBananaImageSize: settings.value.nanoBananaImageSize,
            aspectRatioIndex: settings.value.aspectRatioIndex,
            backgroundImage: settings.value.backgroundImage,
            // Clone uploaded files for regeneration (but don't include in storage)
            uploadedFiles: state.value.uploadedFiles.map(file => ({ ...file })),
          }

          if (response.data.conversation_id)
            state.value.conversationId = response.data.conversation_id

          state.value.generationHistory.push(newGeneration)
          saveToStorage()

          message.success(response.message || 'Image generated successfully!')
          await scrollToBottom()

          // Clear uploaded files after successful generation
          state.value.uploadedFiles.forEach((file) => {
            if (file.url?.startsWith('blob:'))
              URL.revokeObjectURL(file.url)
          })
          state.value.uploadedFiles = []

          if (!settings.value.keepPrompt)
            state.value.prompt = ''
        }
        else {
          message.error('No images were generated. Please try again.')
        }
      }
    }
    catch (error) {
      // Error handling
    }
    finally {
      state.value.isGenerating = false
      await scrollToBottom()
    }
  }

  // Create new generation session
  const onGenerateImage = () => {
    state.value.conversationId = null
    state.value.generationHistory = []
    clearStorage()
  }

  // Regenerate image
  const regenerateImage = async (generation: GenerationItem) => {
    // Restore basic settings
    state.value.prompt = generation.query
    settings.value.selectedModel = generation.model as any
    settings.value.selectedImageSize = generation.image_size as any

    // Restore model-specific settings
    if (generation.image_style)
      settings.value.selectedStyle = generation.image_style as any

    if (generation.negativePrompt !== undefined)
      settings.value.negativePrompt = generation.negativePrompt

    if (generation.ns11Style !== undefined)
      settings.value.ns11Style = generation.ns11Style as any

    if (generation.ns11Pages !== undefined)
      settings.value.ns11Pages = generation.ns11Pages as any

    if (generation.nanoBananaImageSize !== undefined)
      settings.value.nanoBananaImageSize = generation.nanoBananaImageSize

    if (generation.aspectRatioIndex !== undefined)
      settings.value.aspectRatioIndex = generation.aspectRatioIndex

    if (generation.backgroundImage !== undefined)
      settings.value.backgroundImage = generation.backgroundImage

    // Restore uploaded files if available
    if (generation.uploadedFiles && generation.uploadedFiles.length > 0)
      state.value.uploadedFiles = generation.uploadedFiles.map(file => ({ ...file }))

    // Update file upload visibility based on model
    updateFileUploadVisibility()

    await generateImage()
  }

  // Delete generation
  const deleteGeneration = (generationId: string) => {
    dialog.create({
      title: t('imageGeneration.deleteMessage'),
      content: t('imageGeneration.deleteMessageConfirm'),
      positiveText: t('common.delete'),
      negativeText: t('common.cancel'),
      closable: false,
      onPositiveClick: async () => {
        try {
          await deleteConversationMessage(generationId)
          state.value.generationHistory = state.value.generationHistory.filter(g => g.id !== generationId)
          saveToStorage()
          message.success(t('imageGeneration.deleteMessageSuccess'))
        }
        catch (error) {
          // Handle error
        }
      },
    })
  }

  // Load conversation history
  const loadConversationHistory = (conversationHistory: any[]) => {
    const mappedHistory = conversationHistory.map(item => ({
      id: String(item.id),
      query: item.query,
      images: (item.files || []).map((file: any) => ({
        id: String(file.id),
        url: file.url,
        agent_generate_image_history_id: file.agent_generate_image_history_id,
        is_deleted: file.is_deleted,
      })),
      model: item.model || 'DALL_2',
      image_style: item.image_style || 'vivid',
      image_size: item.image_size || 'small',
      number_of_image: item.number_of_image,
      conversation_id: item.conversation_id,
      generation_time: item.generation_time,
      created_at: item.created_at,
    }))

    state.value.generationHistory = mappedHistory

    if (conversationHistory.length > 0)
      state.value.conversationId = conversationHistory[0].conversation_id

    saveToStorage()
    scrollToBottom()
  }

  // Update showFileUpload based on model selection
  const updateFileUploadVisibility = () => {
    state.value.showFileUpload = settings.value.selectedModel === 'GPT_IMAGE_EDIT'
      || settings.value.selectedModel === 'NANO_BANANA'
      || settings.value.selectedModel === 'NANO_BANANA_PRO'
    if (!state.value.showFileUpload) {
      state.value.uploadedFiles.forEach((file) => {
        if (file.url?.startsWith('blob:'))
          URL.revokeObjectURL(file.url)
      })
      state.value.uploadedFiles = []
    }
  }

  return {
    // State
    state,
    settings,

    // Functions
    saveToStorage,
    loadFromStorage,
    clearStorage,
    scrollToBottom,
    getImageSizeOptions,
    generateImage,
    onGenerateImage,
    regenerateImage,
    deleteGeneration,
    loadConversationHistory,
    updateFileUploadVisibility,
  }
}
